import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { BackLink } from "@/components/BackLink";
import { LegalDocView } from "@/components/legal/LegalDocView";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/EmptyState";
import { api } from "@/lib/api";
import { BRAND } from "@/config/brand";
import { usePageMeta } from "@/lib/seo";

/* Static Legal Engine documents (Platform Terms, Compliance Declaration, and
   any future registry additions) rendered at /legal/<route>. */
export default function LegalEnginePage({ docKey }) {
  const { t, i18n } = useTranslation();
  const [doc, setDoc] = useState(undefined);
  usePageMeta({
    title: doc?.title ? `${doc.title} — ${BRAND.name}` : undefined,
    description: doc?.summary || doc?.title,
    localized: true,
  });

  useEffect(() => {
    setDoc(undefined);
    api
      .get(`/legal/documents/${docKey}`, { params: { locale: i18n.resolvedLanguage } })
      .then(({ data }) => setDoc(data))
      .catch(() => setDoc(null));
  }, [docKey, i18n.resolvedLanguage]);

  if (doc === undefined) {
    return (
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16 space-y-6" data-testid="legal-doc-skeleton">
        <Skeleton className="h-10 w-2/3 rounded-xl" />
        <Skeleton className="h-72 w-full rounded-3xl" />
      </div>
    );
  }
  if (doc === null) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24">
        <EmptyState title={t("legal.notFoundTitle")} description={t("legal.notFoundBody")} />
      </div>
    );
  }
  return (
    <div>
      <div className="max-w-6xl mx-auto px-6 lg:px-12 pt-8">
        <BackLink />
      </div>
      <LegalDocView doc={doc} />
    </div>
  );
}
